import { useEffect, useMemo } from 'react';
import type { UseFormReturn } from 'react-hook-form';
import type { EventPanelFormType, AttributeOption, CallForServiceOption } from '@mark43/cad-api';
import { useIntegratedForm } from '~/forms/use-integrated-form';
import { useBootstrapState } from '~/bootstrap/bootstrap-provider';

type DraftMeta =
    | {
          callForServiceId?: number;
          riskLevelAttributeId?: number;
          priorityAttributeId?: number;
      }
    | null
    | undefined;

type Params = {
    eventId?: number;
    eventForm: UseFormReturn<EventPanelFormType>;
    eventPanelOptions?: {
        riskLevelOptions?: AttributeOption[];
        priorityOptions?: AttributeOption[];
    };
    callForServiceOptions?: CallForServiceOption[];
    eventDraftMetaData?: DraftMeta;
    currentProQaTempId?: string;
    eventDraftProQaTempId?: string;
};

type MappingEntry = {
    entity: 'callForService' | 'riskLevel' | 'priority';
    id: number | null;
    options: (AttributeOption | CallForServiceOption)[];
};

export function useEventDraftProQaMetadataSync({
    eventId,
    eventForm,
    eventPanelOptions,
    callForServiceOptions,
    eventDraftMetaData,
    currentProQaTempId,
    eventDraftProQaTempId,
}: Params): void {
    const { onSubmit } = useIntegratedForm<EventPanelFormType>();
    const { eventPanelConfiguration } = useBootstrapState();

    const mappings: MappingEntry[] = useMemo(() => {
        if (
            eventId ||
            !eventPanelOptions ||
            !callForServiceOptions ||
            currentProQaTempId !== eventDraftProQaTempId
        ) {
            return [];
        }

        return [
            {
                entity: 'callForService',
                id: eventDraftMetaData?.callForServiceId ?? null,
                options: callForServiceOptions ?? [],
            },
            {
                entity: 'riskLevel',
                id: eventDraftMetaData?.riskLevelAttributeId ?? null,
                options: eventPanelOptions?.riskLevelOptions ?? [],
            },
            {
                entity: 'priority',
                id: eventDraftMetaData?.priorityAttributeId ?? null,
                options: eventPanelOptions?.priorityOptions ?? [],
            },
        ];
    }, [
        eventId,
        eventDraftMetaData?.callForServiceId,
        eventDraftMetaData?.riskLevelAttributeId,
        eventDraftMetaData?.priorityAttributeId,
        callForServiceOptions,
        eventPanelOptions,
        currentProQaTempId,
        eventDraftProQaTempId,
    ]);

    useEffect(() => {
        if (mappings.length === 0) {
            return;
        }

        let hasSetValue = false;
        let timeoutId: NodeJS.Timeout | undefined;

        for (const { entity, id, options } of mappings) {
            if (!id || !options?.length || eventForm.formState?.dirtyFields?.[entity]) {
                continue;
            }

            const entityFormField = options.find((o) => o?.value === id);
            if (!entityFormField) {
                continue;
            }

            const curr = eventForm.getValues(entity);
            if (curr?.value === entityFormField.value) {
                continue;
            }

            eventForm.setValue(entity, entityFormField, { shouldDirty: false, shouldTouch: false });
            hasSetValue = true;
        }

        if (hasSetValue) {
            timeoutId = setTimeout(async () => {
                const emergencyCommunication = eventPanelConfiguration?.eventCommunicationEmergency;
                if (emergencyCommunication) {
                    eventForm.setValue('eventCommunication', emergencyCommunication);
                }
                onSubmit(undefined);
            }, 0);
        }

        return () => {
            if (timeoutId) {
                clearTimeout(timeoutId);
            }
        };
    }, [mappings, eventForm, onSubmit, eventPanelConfiguration, eventId]);
}
